import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReservationManageService } from "../../services/ReservationManageService";
import { UserManageService } from "../../services/UserManageService";
import { SchoolService } from "../../services/SchoolService";
import { DateTime } from "luxon";

const reservationService = new ReservationManageService();
const userService = new UserManageService();
const schoolService = new SchoolService();

export default function ReservationEdit() {
  const navigate = useNavigate();
  const { reservationId } = useParams();
  const isEditMode = !!reservationId;

  const [formData, setFormData] = useState({
    userId: "",
    schoolId: "",
    date: "",
    startDate: "",
    endDate: "",
  });

  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch users and schools
    Promise.all([
      userService.find({ itemsPerPage: 300, pagination: false }),
      schoolService.find({
        itemsPerPage: 300,
        active: "all",
        pagination: false,
      }),
    ])
      .then(([usersResponse, schoolsResponse]) => {
        setUsers(usersResponse.items);
        setSchools(schoolsResponse.items);
      })
      .catch((error) => {
        console.error("Erro ao carregar dados:", error);
        setError("Não foi possível carregar os dados necessários.");
      })
      .finally(() => setLoading(false));

    // If editing, fetch reservation data
    if (isEditMode) {
      reservationService
        .findOne(reservationId)
        .then((reservation) => {
          setFormData({
            userId: reservation.userId,
            schoolId: reservation.school._id,
            date: DateTime.fromISO(reservation.date).toFormat("yyyy-MM-dd"),
            startDate: DateTime.fromISO(reservation.startHour)
              .setZone("America/Sao_Paulo")
              .toFormat("HH:mm"),
            endDate: DateTime.fromISO(reservation.endHour)
              .setZone("America/Sao_Paulo")
              .toFormat("HH:mm"),
          });
        })
        .catch((error) => {
          console.error("Erro ao carregar reserva:", error);
          setError("Não foi possível carregar os dados da reserva.");
        });
    }
  }, [reservationId, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateReservation = () => {
    const { date, startDate, endDate } = formData;
    let startDateTime = DateTime.fromFormat(
      `${date} ${startDate}`,
      "yyyy-MM-dd HH:mm"
    );
    let endDateTime = DateTime.fromFormat(
      `${date} ${endDate}`,
      "yyyy-MM-dd HH:mm"
    );

    // Detecta reserva overnight (pernoite)
    // Se hora de término < hora de início, é overnight e termina no dia seguinte
    const isOvernight = endDateTime <= startDateTime;
    if (isOvernight) {
      endDateTime = endDateTime.plus({ days: 1 });
    }

    const duration = endDateTime.diff(startDateTime, "hours").hours;
    if (duration < 1) {
      return "A duração mínima da reserva deve ser de 1 hora.";
    }

    if (duration > 24) {
      return "A duração máxima da reserva deve ser de 24 horas.";
    }

    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationError = validateReservation();
    if (validationError) {
      alert(validationError);
      return;
    }

    const { date, startDate, endDate } = formData;
    let startDateTime = DateTime.fromFormat(
      `${date} ${startDate}`,
      "yyyy-MM-dd HH:mm"
    );
    let endDateTime = DateTime.fromFormat(
      `${date} ${endDate}`,
      "yyyy-MM-dd HH:mm"
    );

    // Detecta reserva overnight (pernoite)
    // Se hora de término < hora de início, é overnight e termina no dia seguinte
    const isOvernight = endDateTime <= startDateTime;
    if (isOvernight) {
      endDateTime = endDateTime.plus({ days: 1 });
    }

    // Converter para UTC
    startDateTime = startDateTime.toUTC();
    endDateTime = endDateTime.toUTC();

    const reservationData = {
      ...formData,
      startDate: startDateTime.toISO(),
      endDate: endDateTime.toISO(),
      date: DateTime.fromFormat(date, "yyyy-MM-dd").toUTC().toISO(),
    };


    if (isEditMode) {
      reservationService
        .update(reservationId, reservationData)
        .then(() => {
          alert("Reserva atualizada com sucesso!");
          navigate("/admin/reservations");
        })
        .catch((error) => {
          console.error("Erro ao atualizar reserva:", error);
          alert("Não foi possível atualizar a reserva.");
        });
    } else {
      reservationService
        .create(reservationData)
        .then(() => {
          alert("Reserva criada com sucesso!");
          navigate("/admin/reservations");
        })
        .catch((error) => {
          console.error("Erro ao criar reserva:", error);
          alert("Não foi possível criar a reserva.");
        });
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Carregando...</div>;
  }

  if (error) {
    return <div className="text-danger mt-5">{error}</div>;
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">
          {isEditMode ? "Editar Reserva" : "Criar Reserva"}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Usuário</label>
          <select
            name="userId"
            value={formData.userId}
            onChange={handleInputChange}
            className="form-select"
            required
          >
            <option value="">Selecione um usuário</option>
            {users.map((user) => (
              <option key={user._id} value={user._id}>
                {user.username} - {user.name} {user.surname} {user.lastName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Unidade Escolar</label>
          <select
            name="schoolId"
            value={formData.schoolId}
            onChange={handleInputChange}
            className="form-select"
            required
          >
            <option value="">Selecione uma unidade escolar</option>
            {schools.map((school) => (
              <option key={school._id} value={school._id}>
                {school.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Data</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Hora de Início</label>
          <input
            type="time"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Hora de Término</label>
          <input
            type="time"
            name="endDate"
            value={formData.endDate}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>

        <button type="submit" className="btn btn-primary">
          {isEditMode ? "Atualizar Reserva" : "Criar Reserva"}
        </button>
        <button
          type="button"
          className="btn btn-secondary ms-2"
          onClick={() => navigate("/admin/reservations")}
        >
          Cancelar
        </button>
      </form>
    </div>
  );
}
